// Promote this thread's watches + scorecard into a benchmark kit.
// Prefer updating an existing benchmark in the same orchestration group.
import { useClient } from '@cortex/react-query';
import type {
  Benchmark,
  BenchmarkKit,
  PersistentAgentWatch,
  TrialScorecardFact,
} from '@cortex/types';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { routes } from '../../lib/constants';
import { Modal } from '../common/Modal';

function watchToKit(w: PersistentAgentWatch): BenchmarkKit['watches'][number] {
  return {
    name: w.name,
    mcpConnectionId: w.mcpConnectionId,
    toolName: w.toolName,
    args: (w.args as Record<string, unknown> | null) ?? {},
    intervalSec: w.intervalSec,
    laneId: w.laneId ?? w.name,
    wakeDelivery: w.wakeDelivery ?? 'when-idle',
    // DEV-17326: promoted kits keep the watch's situational anchors.
    ...(w.anchors ? { anchors: w.anchors } : {}),
    ...(w.wakePrompt ? { wakePrompt: w.wakePrompt } : {}),
    ...(w.selector ? { selector: w.selector } : {}),
    ...(w.condition
      ? {
          condition: {
            op: w.condition.op,
            value: w.condition.value,
            risingEdge: w.condition.risingEdge === true,
            ...(w.condition.path ? { path: w.condition.path } : {}),
          },
        }
      : {}),
    ...(w.code ? { code: w.code } : {}),
    ...(w.bindings ? { bindings: w.bindings as Record<string, unknown> } : {}),
    ...(w.timeoutMs ? { timeoutMs: w.timeoutMs } : {}),
  };
}

function scorecardFromFact(board: TrialScorecardFact): NonNullable<BenchmarkKit['scorecard']> {
  const deadlineSeconds = board.deadlineAt
    ? Math.max(60, Math.round((new Date(board.deadlineAt).getTime() - Date.now()) / 1000))
    : 1200;
  return {
    ...(board.objective ? { objective: board.objective } : {}),
    deadlineSeconds,
    senses: board.senses.map((s) => ({
      id: s.id,
      mcpConnectionId: s.mcpConnectionId,
      toolName: s.toolName,
      args: (s.args as Record<string, unknown> | undefined) ?? {},
      intervalSec: s.intervalSec ?? 10,
    })),
    metrics: board.metrics.map((m) => ({
      id: m.id,
      ...(m.label ? { label: m.label } : {}),
      senseId: m.senseId,
      selector: m.selector,
      aggregate: m.aggregate ?? 'value',
      comparator: m.comparator,
      target: m.target,
      baselineMode: m.baselineMode ?? 'delta',
      kind: m.kind ?? 'outcome',
    })),
    drive: board.drive
      ? {
          senseId: board.drive.senseId,
          selector: board.drive.selector,
          op: board.drive.op,
          value: board.drive.value,
          wakeDelivery:
            board.drive.wakeDelivery === 'next-turn' ? 'when-idle' : board.drive.wakeDelivery,
          coalesceSec: board.drive.coalesceSec ?? 60,
          ...(board.drive.wakePrompt ? { wakePrompt: board.drive.wakePrompt } : {}),
        }
      : null,
  };
}
export function PromoteKitButton({
  agentId,
  threadId,
  className = 'btn btn--secondary btn--sm',
}: {
  agentId: string;
  threadId: string;
  className?: string;
}) {
  const client = useClient();
  const [open, setOpen] = useState(false);
  const [benchmarkId, setBenchmarkId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);

  const base = `v1/persistent-agent-groups/${agentId}/agents/${threadId}`;

  const definition = useQuery({
    queryKey: ['persistent-agent-definition', agentId],
    queryFn: () =>
      client.http.get(`v1/persistent-agent-groups/${agentId}`).json<{
        orchestrationGroupId?: string | null;
      }>(),
    enabled: open,
  });

  const groupId = definition.data?.orchestrationGroupId ?? '';

  const watches = useQuery({
    queryKey: ['persistent-agent-watches', threadId],
    queryFn: () => client.http.get(`${base}/watches`).json<PersistentAgentWatch[]>(),
    enabled: open,
  });

  const scorecard = useQuery({
    queryKey: ['pa-trial-scorecard', threadId],
    queryFn: () => client.http.get(`${base}/scorecard`).json<TrialScorecardFact | null>(),
    enabled: open,
  });

  const benchmarks = useQuery({
    queryKey: ['benchmarks'],
    queryFn: () => client.http.get('v1/benchmarks').json<Benchmark[]>(),
    enabled: open,
  });

  const kit: BenchmarkKit | null = useMemo(() => {
    if (!watches.isSuccess && !scorecard.isSuccess) return null;
    const list = watches.data ?? [];
    const sc = scorecard.data;
    return {
      watches: list.map(watchToKit),
      scorecard: sc?.senses?.length && sc.metrics?.length ? scorecardFromFact(sc) : null,
    };
  }, [watches.isSuccess, watches.data, scorecard.isSuccess, scorecard.data]);

  const groupBenchmarks = useMemo(() => {
    const all = benchmarks.data ?? [];
    if (!groupId) return all;
    return all.filter((b) => b.orchestrationGroupId === groupId);
  }, [benchmarks.data, groupId]);

  const save = useMutation({
    mutationFn: async () => {
      if (!kit) throw new Error('Kit not loaded');
      if (!benchmarkId) throw new Error('Pick a benchmark');
      if (!kit.watches.length && !kit.scorecard) {
        throw new Error('This thread has no watches or scorecard to save');
      }
      return client.http.patch(`v1/benchmarks/${benchmarkId}`, { json: { kit } }).json<Benchmark>();
    },
    onSuccess: (b) => {
      setError(null);
      setSavedId(b.id);
    },
    onError: (err) => {
      setSavedId(null);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const empty = kit && kit.watches.length === 0 && !kit.scorecard;

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => {
          setOpen(true);
          setError(null);
          setSavedId(null);
          setBenchmarkId('');
        }}
      >
        Save as benchmark kit…
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Save as benchmark kit" width={480}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p className="t-sm t-secondary" style={{ margin: 0 }}>
            Copy this thread&apos;s watches and scorecard onto a benchmark. Trials install that kit
            on each launched thread at start.
          </p>

          {(watches.isLoading || scorecard.isLoading) && (
            <span className="t-sm t-muted">Loading thread kit…</span>
          )}

          {kit && (
            <div className="card" style={{ padding: 10 }}>
              <div className="t-sm">
                <strong>{kit.watches.length}</strong> watch
                {kit.watches.length === 1 ? '' : 'es'}
                {kit.scorecard ? (
                  <div className="t-xs t-muted">
                    · scorecard {kit.scorecard.metrics.length} metric(s)
                    {kit.scorecard.drive ? ' + drive' : ''}
                  </div>
                ) : (
                  <span className="t-xs t-muted"> · no scorecard</span>
                )}
              </div>
              {kit.watches.length > 0 && (
                <ul className="t-xs t-muted" style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {kit.watches.slice(0, 8).map((w) => (
                    <li key={w.name}>
                      {w.name} · {w.toolName}
                      {w.selector ? ` · ${w.selector}` : ''} → {w.laneId}
                    </li>
                  ))}
                  {kit.watches.length > 8 ? <li>…+{kit.watches.length - 8} more</li> : null}
                </ul>
              )}
            </div>
          )}

          {empty && (
            <p className="t-sm t-muted" style={{ margin: 0 }}>
              Nothing to promote — add a scorecard or watches on this thread first.
            </p>
          )}

          <label
            className="t-xs t-muted"
            style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            Benchmark
            <select
              className="input"
              value={benchmarkId}
              onChange={(e) => setBenchmarkId(e.target.value)}
              disabled={Boolean(empty)}
            >
              <option value="">Select…</option>
              {groupBenchmarks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                  {b.kit?.scorecard ? ' · has scorecard' : ''}
                </option>
              ))}
            </select>
          </label>

          {groupId && groupBenchmarks.length === 0 && (
            <p className="t-xs t-muted" style={{ margin: 0 }}>
              No benchmarks in this orchestration group yet. Create one under Orchestration →
              Benchmarks, then come back.
            </p>
          )}

          {error && (
            <p className="t-sm" style={{ margin: 0, color: 'var(--danger, #c44)' }}>
              {error}
            </p>
          )}
          {savedId && (
            <p className="t-sm" style={{ margin: 0 }}>
              Saved. <Link to={routes.orchestrationBenchmark(savedId)}>Open benchmark</Link>
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setOpen(false)}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!kit || empty || !benchmarkId || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'Saving…' : 'Save kit'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
